import { faPlusCircle } from "@fortawesome/fontawesome-free-solid";
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { clientDb } from "../../../../shared/firebase";
import CheckboxList from "../../../core/CheckboxList";
import { Section } from "../../../core/CheckboxList/interfaces";
import SearchInput from "../../../core/SearchInput";
import DashboardBlock from "../../../DashboardBlock";
import Dialog, { DialogConfigSetter } from "../../../Dialog";
import ChipsList from "../../../general/Chips/ChipsList";
import ModalHeader from "../../../menus/modal/modal-header";
import Loader from "../../Billing/Other/Loader";
import AddGroupOverlay from "./AddGroupOverlay";
import CreateNewGroupModal from "./CreateNewGroupModal/CreateNewGroupModal";
import styles from './GroupsEditPage.module.scss';

const GroupsEditPage = () => {
  const { id: personId } = useParams<{id: string}>();
  const [createGroup, setCreateGroup] = React.useState(false);
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [personGroups, setPersonGroups] = useState([]);
  const [personGroupIds, setPersonGroupIds] = useState([]);
  const [initialPersonGroupIds, setInitialPersonGroupIds] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState('');
  const [personIsSubAdmin, setPersonIsSubAdmin] = useState(false);

  const [isCreateNewGroupModalOpen, setIsCreateNewGroupModalOpen] = useState(false);

  const [searchValue, setSearchValue] = useState<string>('');

  const dialogRef = React.useRef<DialogConfigSetter>();
  const setupDialog = (callBack: () => DialogConfigSetter) => dialogRef.current = callBack();

  const navigation = useHistory();

  const fetchGroups = async () => {
    setIsLoading(true);
    try {
      const groupsRaw = (await (clientDb().child(`/groups`).once('value'))).val();
      const personGroupsRaw = (await (clientDb().child(`/people/people-details/${personId}/groups`).once('value'))).val();

      const personGroups = personGroupsRaw ? Object.keys(groupsRaw)
        .filter(group => Object.keys(personGroupsRaw).includes(group))
        .map(group => {
          return {
            group,
            name: groupsRaw[group].name
          }
        }) : [];

      const groups = groupsRaw ? Object.keys(groupsRaw).map(group => {
        return {
          group,
          name: groupsRaw[group].name
        }
      }) : [];

      const personSubAdmin = (await (clientDb().child(`/acl/can/sub-admin/${personId}`).once('value'))).val();
      const isSubAdmin = personSubAdmin ? true : false;

      setGroups(groups);
      setPersonGroups(personGroups);
      if(personGroups.length) setPersonGroupIds(Object.keys(personGroupsRaw));
      if(personGroups.length) setInitialPersonGroupIds(Object.keys(personGroupsRaw));
      setAllGroups(groups);
      setPersonIsSubAdmin(isSubAdmin);
    }
    catch (error) {
      console.error(error);
    }
    finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchGroups();
  }, []);

  const groupClicked = (groupId) => {
    if (!personGroupIds.includes(groupId)) {
      setPersonGroupIds(ids => [...ids, groupId]);
      const newGroup = groups.find(group => group.group === groupId);
      setPersonGroups(groups => [...groups, newGroup]);
    }
  }

  const onFilterChange = async (newValue) => {
    setGroupFilter(newValue);
    setSearchValue(newValue);
    if(newValue){
      const filteredGroups = allGroups.filter(group => {
        return group.name.toLowerCase().includes(newValue.toLowerCase())
      });

      setGroups(filteredGroups)
    }
    else {
      setGroups(allGroups);
    }
  }

  const onRemove = async (groupId) => {
    const dialog = dialogRef.current;
    const currentGroupName = personGroups.find((group) => group.group === groupId).name;

    const confirmed = await dialog?.({
      type: 'CONFIRM',
      title: 'Warning!',
      body:
        <div>
          <p>This will remove the group <b>${currentGroupName}</b> from user.</p>
          <p>Click OK to confirm.</p>
        </div>
    });
    if (!confirmed) return;
    setPersonGroupIds(ids => ids.filter(id => id !== groupId));
    setPersonGroups(personGroups.filter(group => group.group !== groupId));
  }

  const groupToggled = (groupId) => {
    if (personGroupIds.includes(groupId)) {
      onRemove(groupId);
    } else {
      groupClicked(groupId);
    }
  };

  const selectAllGroups = () => {
    const allGroupIds = groups.map(group => group.group);
    setPersonGroupIds(allGroupIds);
    setPersonGroups(groups);
  };

  const updateGroups = async () => {
    const groupsToBeAdded = [];
    const groupsToBeDeleted = [];

    for (const groupId of personGroupIds) {
      if(!initialPersonGroupIds.includes(groupId)) groupsToBeAdded.push(groupId);
    }

    for (const groupId of initialPersonGroupIds) {
      if(!personGroupIds.includes(groupId)) groupsToBeDeleted.push(groupId);
    }

    const deleteGroupsObject = {};
    const addGroupsObject = {};
    const collection = personIsSubAdmin ? 'sub-admins' : 'sub-users';

    for (const group of groupsToBeDeleted) {
      deleteGroupsObject[`groups/${group}/${collection}/${personId}`] = null;
    }

    for (const group of groupsToBeAdded) {
      addGroupsObject[`groups/${group}/${collection}/${personId}`] = true;
    }

    const groupsObject = personGroupIds.reduce((obj, id) => {
      obj[id] = true;
      return obj;
    }, {});

    await clientDb().update({
      [`people/people-details/${personId}/groups`]: groupsObject
    });

    setInitialPersonGroupIds(personGroupIds)

    await clientDb().update(deleteGroupsObject);
    await clientDb().update(addGroupsObject);
  }

  const mappedGroupsList: Section[] = groups.map((groupItem, id) => {
    return {
      id: id.toString(),
      value: [{id: groupItem.group, name: groupItem.name, onChange: () => groupToggled(groupItem.group)}]
    }
  })


  return (
    <DashboardBlock>
      {createGroup && <AddGroupOverlay
          close={() => setCreateGroup(false)}
          onUpdate={fetchGroups}
      />}
      <ModalHeader
        title='Groups'
        action={
          {
            click: () => setIsCreateNewGroupModalOpen(true),
            fa: faPlusCircle,
            title: 'Add new group'
          }
        }
      />
      {isLoading ? <Loader color='inherit' size={24} className={styles.loader} />
        : <>
          <section className={styles.selectedDeviceContainer}>
            <p>Select Groups:</p>
            <p className={styles.selectedCount}>{personGroups.length} Groups Selected</p>
          </section>

          <SearchInput
            value={searchValue}
            onChange={onFilterChange}
            placeholder='Filter Groups'
            className={styles.searchInput}
          />

          <CheckboxList
              sections={mappedGroupsList}
              selected={personGroupIds}
              changeAllItems={selectAllGroups}
              isAllSelected={false}
              containerClassName={styles.checkboxesList}
            />
          <section className={styles.selectedGroups}>
            <p>Selected Groups:</p>
            <ChipsList
              data={personGroups.map(group => ({
                id: group.group,
                title: group.name
              }))}
              onRemoveItem={onRemove}
            />
          </section>
        </>
      }
      <section className={styles.bottomButtons}>
        <div className={styles.actionButtons}>
          <button className={`${styles.btn} ${styles.secondary}`} onClick={() => navigation.goBack()}>
            Cancel
          </button>
          <button className={`${styles.btn} ${styles.primary}`} onClick={updateGroups}>
            Update Groups
          </button>
        </div>
      </section>
      <Dialog setupConfig={setupDialog} />
      {(isCreateNewGroupModalOpen) && (
        <CreateNewGroupModal
          setIsPublicLinkModalOpen={setIsCreateNewGroupModalOpen}
          onUpdate={fetchGroups}
        />
      )}
    </DashboardBlock>
  );
};

export default GroupsEditPage;
